import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { Course } from '@/payload/payload-types';
import ChevronRight from '../../../_icons/Chevron-right';
import RichTextLexical from '../../RichTextLexical';
import { useScroll } from '../../../_providers/ScrollProvider';
import { Button } from '../../Button';
import ChevronDown from '../../../_icons/Chevron-down';

const PrerequisiteRow: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="flex">
		<p className="text-muted-navy mr-4 w-40 shrink-0 font-bold">{label}</p>
		<div className="flex-1">{children}</div>
	</div>
);

export const Prerequisites: React.FC<{
	course: Course;
}> = props => {
	const t = useTranslations('Course');
	const { scrollToContactSection } = useScroll();
	const { course } = props;
	const hasCourseSalesRep = course.courseSalesRep && typeof course.courseSalesRep !== 'string';

	const prerequisites = course.prerequisitesGroup;
	const hasPrerequisites = Boolean(
		prerequisites?.showKnowledgePrerequisite ||
			prerequisites?.showEducationPrerequisite ||
			prerequisites?.showExperiencePrerequisite ||
			prerequisites?.showPrereqCourse,
	);

	if (typeof prerequisites?.prereqCourse === 'string') {
		throw new Error('Bad depth');
	}

	type PrerequisitesGroup = Course['prerequisitesGroup'];
	const translateEducation = (prereqs: PrerequisitesGroup) => {
		const translatedKeys = {
			highschoolDiploma: t('highschoolDiploma'),
			bachelors: t('bachelors'),
			masters: t('masters'),
			doctoral: t('doctoral'),
		};

		if (!prereqs?.education) {
			return '';
		}
		return translatedKeys[prereqs?.education];
	};

	return (
		<Fragment>
			{hasPrerequisites ? (
				<Fragment>
					<div className="space-y-2">
						<div className="space-y-2">
							{prerequisites?.showPrereqCourse &&
								(prerequisites?.prereqCourse?.title ||
									prerequisites?.prereqCourseText) && (
									<PrerequisiteRow label={t('course')}>
										{prerequisites?.prereqCourse?.title ||
											prerequisites?.prereqCourseText}
									</PrerequisiteRow>
								)}
							{prerequisites?.showEducationPrerequisite &&
								(prerequisites?.education ||
									prerequisites?.customEducationLexical) && (
									<PrerequisiteRow label={t('education')}>
										{translateEducation(prerequisites) || (
											<RichTextLexical
												content={prerequisites?.customEducationLexical}
											/>
										)}
									</PrerequisiteRow>
								)}
							{prerequisites?.showKnowledgePrerequisite &&
								prerequisites?.knowledgeRichTextLexical && (
									<PrerequisiteRow label={t('prerequisite-knowledge')}>
										<RichTextLexical
											content={prerequisites?.knowledgeRichTextLexical}
										/>
									</PrerequisiteRow>
								)}
							{prerequisites?.showExperiencePrerequisite &&
								prerequisites?.experienceRichTextLexical && (
									<PrerequisiteRow label={t('prerequisite-experience')}>
										<RichTextLexical
											content={prerequisites?.experienceRichTextLexical}
										/>
									</PrerequisiteRow>
								)}
						</div>
					</div>
				</Fragment>
			) : (
				<p>{t('no-prerequisites-needed')}</p>
			)}
			<Fragment>
				{course.showParticipantProfile && (
					<div>
						<h3 className="text-gold-light mt-12 mb-4 text-2xl leading-10 font-bold">
							Osaleja profiil
						</h3>
						<RichTextLexical content={course.participantProfile} />
					</div>
				)}

				<div className="bg-gold-light/10 mt-12 flex w-full flex-col items-center justify-center rounded-3xl p-11 lg:flex-row">
					<p className="lg:mr-6">{t('not-sure-if-qualify')}</p>
					{hasCourseSalesRep ? (
						<Button
							className="mt-2 lg:mt-0"
							intent="primary"
							variant="ghost"
							onClick={() => {
								scrollToContactSection();
							}}
						>
							{t('contact-us')} <ChevronDown />
						</Button>
					) : (
						<a
							href={`mailto:${course.contactEmail}`}
							target="_blank"
							className="text-muted-navy hover:text-brand-navy flex items-center justify-center space-x-1 font-semibold hover:underline hover:transition-all"
						>
							<span className="mr-1">{t('contact-us')}</span>
							<span>
								<ChevronRight />
							</span>
						</a>
					)}
				</div>
			</Fragment>
		</Fragment>
	);
};
